import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest } from '../config/authConfig';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState(null);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userRole, setUserRole] = useState('user');
  const [allowedSections, setAllowedSections] = useState([]);
  const [userDashboard, setUserDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessToken, setAccessToken] = useState(null);
  
  // Impersonation state - persist in sessionStorage
  const [impersonatedUser, setImpersonatedUser] = useState(() => {
    try {
      const stored = sessionStorage.getItem('impersonatedUser');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [isImpersonating, setIsImpersonating] = useState(() => {
    try {
      return sessionStorage.getItem('isImpersonating') === 'true';
    } catch {
      return false;
    }
  });
  
  // Persist impersonation state to sessionStorage
  useEffect(() => {
    if (isImpersonating && impersonatedUser) {
      sessionStorage.setItem('isImpersonating', 'true');
      sessionStorage.setItem('impersonatedUser', JSON.stringify(impersonatedUser));
    } else {
      sessionStorage.removeItem('isImpersonating');
      sessionStorage.removeItem('impersonatedUser');
    }
  }, [isImpersonating, impersonatedUser]);

  // Get access token
  const getAccessToken = useCallback(async () => {
    if (accounts.length === 0) return null;
    
    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });
      return response.idToken;
    } catch (error) {
      console.error('Silent token acquisition failed:', error);
      return null;
    }
  }, [instance, accounts]);

  // Check authorization with backend
  const checkAuthorization = useCallback(async () => {
    if (!isAuthenticated || accounts.length === 0) {
      setLoading(false);
      return;
    }

    try {
      const token = await getAccessToken();
      if (!token) {
        setLoading(false);
        return;
      }

      setAccessToken(token);

      const response = await fetch(`${API_BASE}/api/auth/check`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUser({
          email: accounts[0].username,
          name: accounts[0].name,
          ...data.user,
        });
        setIsAuthorized(data.authorized);
        setIsAdmin(data.is_admin);
        setUserRole(data.role || (data.is_admin ? 'admin' : 'user'));
        setAllowedSections(data.allowed_sections || []);
        setUserDashboard(data.dashboard || null);

        // Apply theme from preferences
        if (data.preferences?.theme) {
          localStorage.setItem('dashboard-theme', data.preferences.theme);
          // Theme will be applied by ThemeContext on next render
          window.dispatchEvent(new CustomEvent('theme-sync', { detail: data.preferences.theme }));
        }
      } else if (response.status === 403) {
        setUser({
          email: accounts[0].username,
          name: accounts[0].name,
        });
        setIsAuthorized(false);
        setIsAdmin(false);
        setUserRole('user');
        setUserDashboard(null);
      }
    } catch (error) {
      console.error('Authorization check failed:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, accounts, getAccessToken]);

  useEffect(() => {
    if (inProgress === InteractionStatus.None) {
      checkAuthorization();
    }
  }, [isAuthenticated, inProgress, checkAuthorization]);

  // Use REDIRECT flow instead of popup - more reliable
  const login = async () => {
    try {
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Login failed:', error);
      toast.error('Login failed. Please try again.');
    }
  };

  const logout = async () => {
    try {
      setUser(null);
      setIsAuthorized(false);
      setIsAdmin(false);
      setUserRole('user');
      setUserDashboard(null);
      setAllowedSections([]);
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const canViewSection = (sectionId) => {
    if (isAdmin) return true;
    if (userRole === 'project_manager') return true;
    return allowedSections.includes(sectionId);
  };

  const isProjectManager = userRole === 'project_manager';

  // Start impersonating a user (admin only)
  const startImpersonation = useCallback((targetUser) => {
    if (!isAdmin) {
      toast.error('Only admins can impersonate users');
      return;
    }
    
    setImpersonatedUser({
      email: targetUser.email,
      name: targetUser.name || targetUser.email,
      id: targetUser.id,
    });
    setIsImpersonating(true);
    toast.success(`Now viewing as ${targetUser.name || targetUser.email}`);
  }, [isAdmin]);

  // Stop impersonating and return to admin view
  const stopImpersonation = useCallback(() => {
    setImpersonatedUser(null);
    setIsImpersonating(false);
    toast.info('Returned to your account');
  }, []);

  // Get the effective user (impersonated or real)
  const effectiveUser = isImpersonating && impersonatedUser ? impersonatedUser : user;
  const effectiveEmail = effectiveUser?.email;

  const value = {
    user,
    isAuthenticated,
    isAuthorized,
    isAdmin,
    isProjectManager,
    userRole,
    userDashboard,
    allowedSections,
    loading,
    accessToken,
    login,
    logout,
    canViewSection,
    refreshAuth: checkAuthorization,
    getAccessToken,
    // Impersonation
    isImpersonating,
    impersonatedUser,
    effectiveUser,
    effectiveEmail,
    startImpersonation,
    stopImpersonation,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
